import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Warning, CaretUp, CaretDown } from "phosphor-react";
import { Button } from "@/components/ui/button";

export default function RiskViolationsDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [allTrades, setAllTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Read setup filter from URL query params
  const setupFilterFromUrl = searchParams.get('setup');
  
  // Sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const tradesRes = await api.get('/trades');
      setAllTrades(tradesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load trades");
    } finally {
      setLoading(false);
    }
  };

  // Get trades with risk violations using EXACT same logic as AdvancedAnalytics
  const riskViolationTrades = useMemo(() => {
    // First filter to closed trades
    let tradesToAnalyze = allTrades.filter(t => t.status === 'closed');
    
    // Apply setup filter if present
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        tradesToAnalyze = tradesToAnalyze.filter(t => !t.setup_id);
      } else {
        tradesToAnalyze = tradesToAnalyze.filter(t => t.setup_id === setupFilterFromUrl);
      }
    }
    
    // Filter to eligible trades (must have RR data)
    const eligibleTrades = tradesToAnalyze.filter(t => 
      t.rr !== null && t.rr !== undefined
    );
    
    // Risk violation = loss that exceeded -1.3R
    const violations = eligibleTrades.filter(t => 
      t.outcome === 'loss' && t.rr < -1.3
    );
    
    return {
      trades: violations,
      eligibleCount: eligibleTrades.length
    };
  }, [allTrades, setupFilterFromUrl]);

  // Cycle sort
  const cycleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Apply sorting
  const sortedTrades = useMemo(() => {
    let result = [...riskViolationTrades.trades];
    
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        } else if (sortColumn === 'rr') {
          aVal = a.rr ?? 0;
          bVal = b.rr ?? 0;
        } else if (sortColumn === 'profit') {
          aVal = a.profit_usd ?? 0;
          bVal = b.profit_usd ?? 0;
        }
        
        return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      });
    } else {
      // Default sort by RR (worst violations first)
      result.sort((a, b) => (a.rr ?? 0) - (b.rr ?? 0));
    }
    
    return result;
  }, [riskViolationTrades.trades, sortColumn, sortDirection]);

  // Column header component
  const ColumnHeader = ({ label, column }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        sortColumn === column ? 'text-white' : ''
      }`}
      onClick={() => cycleSort(column)}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {sortColumn === column && (
          sortDirection === 'desc' 
            ? <CaretDown size={12} weight="bold" className="text-[#FF3B30]" />
            : <CaretUp size={12} weight="bold" className="text-[#FF3B30]" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
          <p className="text-white/60 text-sm">Loading risk violations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="risk-violations-detail-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/advanced')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Warning size={28} weight="duotone" className="text-[#FF3B30]" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">EXECUTION QUALITY</div>
                <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="page-title">
                  Risk Violations
                </h2>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
            <span className="text-white/60 text-sm font-mono">TOTAL: </span>
            <span className="text-[#FF3B30] font-bold text-lg" data-testid="total-count">{riskViolationTrades.trades.length}</span>
          </div>
        </div>

        {/* Summary Row */}
        <div className="mb-6" data-testid="risk-violations-summary">
          <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
            Summary
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-[#000000] border border-[#FF3B30]/20 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">VIOLATIONS</div>
                <div className="text-xl font-bold font-mono text-[#FF3B30]" data-testid="summary-total">
                  {riskViolationTrades.trades.length}
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">% OF ELIGIBLE</div>
                <div className="text-xl font-bold font-mono text-white" data-testid="summary-percent">
                  {riskViolationTrades.eligibleCount > 0 
                    ? Math.round((riskViolationTrades.trades.length / riskViolationTrades.eligibleCount) * 100)
                    : 0}%
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">CRITERIA</div>
                <div className="text-sm font-mono text-white/60">
                  Loss exceeded -1.3R
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {riskViolationTrades.trades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning size={32} weight="duotone" className="text-[#00E676]/60" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">No Risk Violations</h3>
            <p className="text-white/60">
              Great job! No trades exceeded the planned stop loss significantly.
            </p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full" data-testid="risk-violations-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <th className="p-3">ASSET</th>
                      <th className="p-3">DIRECTION</th>
                      <ColumnHeader label="RR" column="rr" />
                      <ColumnHeader label="PROFIT" column="profit" />
                      <th className="p-3">HAND SIZE</th>
                      <ColumnHeader label="DATE" column="date" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTrades.map((trade) => (
                      <tr
                        key={trade.id}
                        data-testid={`trade-row-${trade.id}`}
                        onClick={() => setSelectedTrade(trade)}
                        className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                          selectedTrade?.id === trade.id ? "bg-white/5" : ""
                        }`}
                      >
                        <td className="p-3">
                          <div className="font-mono text-white font-bold">{trade.asset}</div>
                          <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {trade.direction === "long" ? (
                              <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                            ) : (
                              <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                            )}
                            <span className={`font-mono text-sm uppercase ${
                              trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                            }`}>
                              {trade.direction}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-sm text-[#FF3B30]">
                            {trade.rr != null ? trade.rr.toFixed(2) : '-'}R
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-sm text-[#FF3B30]">
                            {trade.profit_usd != null ? `$${trade.profit_usd.toFixed(2)}` : '-'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-sm text-white/60">
                            {trade.hand_size != null ? `$${trade.hand_size.toFixed(2)}` : '-'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-white/60 text-sm">
                            {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Trade Details Panel */}
            {selectedTrade && (
              <div className="w-[400px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                          {selectedTrade.direction?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">VIOLATION</div>
                        <div className="px-2 py-1 rounded bg-[#FF3B30]/10 text-[#FF3B30] text-sm font-bold">
                          {selectedTrade.rr?.toFixed(2)}R
                        </div>
                      </div>
                    </div>

                    {/* Risk Violation Explanation */}
                    <div className="bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-2">WHY THIS IS A VIOLATION</div>
                      <div className="text-white/80 text-sm">
                        Loss of {selectedTrade.rr?.toFixed(2)}R exceeded the planned stop (-1R) by {Math.abs(((selectedTrade.rr ?? 0) + 1) * 100).toFixed(0)}%.
                      </div>
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTrade.entry && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                          <div className="text-white font-mono font-semibold">${selectedTrade.entry?.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedTrade.exit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">EXIT</div>
                          <div className="text-[#FF3B30] font-mono font-semibold">${selectedTrade.exit?.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedTrade.stop_loss && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">PLANNED STOP</div>
                          <div className="text-white/60 font-mono font-semibold">${selectedTrade.stop_loss?.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedTrade.hand_size && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">HAND SIZE</div>
                          <div className="text-white font-mono font-semibold">${selectedTrade.hand_size?.toFixed(2)}</div>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
